import { useLocation, useNavigate } from "react-router-dom";
import symptoms from "./../../../../assets/images/symptoms.svg";
import medicalHistory from "./../../../../assets/images/medical-history.svg";
import editLight from "./../../../../assets/images/edit.svg";
import others from "./../../../../assets/images/other.svg";
import questions from "./../../../../assets/images/message-question.svg";
import DDxInputSelectorBg from "./../../../../assets/images/ddx-input-selector-bg.webp";
import { ValidLanguages } from "../../utils/types";
import edit from "./../../../../assets/images/edit-dark.svg";
import backArrow from "./../../../../assets/images/chevron-right.svg";
import closeIcon from "./../../../../assets/images/close-outline.svg";
import successful from "./../../../../assets/images/success-animation.webp";
import { useState, useEffect } from "react";
import SkeletonLoader from "./SkeletonLoader";
import { Button } from "antd";
import { JWT_TOKEN, LangSpecificMsg } from "../../utils/constants";
import { trackEvent } from "../../../../utils/helper";
import dayjs from "dayjs";
import { getDecodedSCToken } from "../../symptomCollectorHelper";
import { getLocalStorage } from "../../../../utils/localStorage";

const Summary = () => {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const summary: any = state?.summaryResponse || {};
  const isAppointmentInQueue = state?.isAppointmentInQueue || false;
  // Check if user came from successful page
  const isFromConversation = location.state?.from === "conversation";
  const isFromHomeAppointment = location.state?.from === "home-appointment";

  const jwtToken = new URLSearchParams(location.search).get(JWT_TOKEN) || "";

  const decodedToken: any = getDecodedSCToken(jwtToken);
  const {
    um_id,
    patient_unique_id,
    patient_name,
    patient_contact_no,
    doctor_name,
    hm_id,
    pam_id,
    dp_name,
    dp_id,
  } = decodedToken?.user || {};

  const selectedLang =
    state?.selectedLang || state?.summaryResponse?.language || "english";
  const LangSpecificMsgData =
    LangSpecificMsg[(selectedLang as ValidLanguages) || "english"];
  const avatarImage = state?.avatarImage || "";

  const handleEditSC = () => {
    navigate(`/conversation?jwtToken=${jwtToken}`, {
      state: {
        ...state,
        from: "summary",
        selectedLang: state?.selectedLang || state?.summaryResponse?.language,
        conversation: summary?.conversation,
        avatarImage: avatarImage,
      },
    });
    trackEvent("SC_Patient_EditedSummary", {
      submission_id: state?.id,
      language: selectedLang,
      appointment_id: pam_id,
      hospital_id: hm_id,
      doctor_name: doctor_name,
      doctor_id: um_id,
      patient_id: patient_unique_id,
      patient_name: patient_name,
      patient_mobile_number: patient_contact_no,
      doctor_speciality: dp_name,
      dp_id: dp_id,
      timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    });
  };

  const [isLoading, setIsLoading] = useState(true);
  const [showSubmitBtn, setShowSubmitBtn] = useState(
    state?.showSubmitBtn || false
  );
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (showSubmitBtn) {
      handleSubmitClick();
    }
    const timer = setTimeout(
      () => {
        setIsLoading(false);
      },
      showSubmitBtn ? 2000 : 1000
    );

    return () => clearTimeout(timer);
  }, []);

  const handleSubmitClick = () => {
    // Show success message
    setShowSuccess(true);

    // Hide success message and submit button after 2 seconds
    setTimeout(() => {
      setShowSuccess(false);
      setShowSubmitBtn(false);
    }, 1000);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="max-w-[450px] mx-auto bg-white min-h-screen pb-[80px]">
      {/* Sticky Header */}
      <div className="sticky top-[-1px] z-50 bg-white">
        <div className="flex items-center gap-3 p-4">
          {isFromHomeAppointment ? (
            <button onClick={handleBack}>
              <img src={backArrow} alt="back" className="rotate-180 w-6 h-6" />
            </button>
          ) : (
            <button
              onClick={() =>
                navigate(`/appointment-booking?jwtToken=${jwtToken}`)
              }
            >
              <img src={closeIcon} alt="close-icon" className="w-6 h-6" />
            </button>
          )}
          <p className="text-[#1F2933] flex-1 font-semibold text-base">
            Symptoms Shared to Doctor
          </p>
        </div>
      </div>

      {/* Content with Background */}
      <div className="relative">
        {/* Background Image */}
        <div className="fixed inset-0 top-[60px]">
          <img
            src={DDxInputSelectorBg}
            alt="background"
            className="h-full w-full object-cover opacity-5"
          />
        </div>

        {/* Conditional Render: Loader or Content */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <div
            className="relative z-10"
            style={{ overflowY: "auto", height: "100vh" }}
          >
            <div className="flex items-center gap-3 p-4 mt-4 pb-0">
              <div className="relative w-[38px] h-[38px] bg-[#8329f11a] rounded-full flex items-center justify-center p-1 overflow-hidden">
                <img
                  src={avatarImage}
                  alt={`${selectedLang}-strip`}
                  className="rounded-full mb-[-8px]"
                />
              </div>
              <p className="text-gray-600 flex-1 text-sm">
                Thank you! Your symptoms has been successfully shared with the
                Doctor!
              </p>
            </div>
            {/* Symptoms Section */}
            {summary?.symptoms?.length > 0 && (
              <div className="p-4 bg-white rounded-lg mx-4 mt-4 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5">
                    <img
                      src={symptoms}
                      alt="symptoms"
                      className="w-full h-full"
                    />
                  </div>
                  <h2 className="text-base font-medium text-gray-900">
                    Symptoms
                  </h2>
                </div>
                <div className="space-y-6">
                  {summary?.symptoms?.map((symptom: any, index: any) => (
                    <div key={index} className="ml-3 relative pl-4">
                      <div className="absolute left-0 top-[10px] w-1.5 h-1.5 bg-gray-400 rounded-full mt-[-2px]"></div>

                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {symptom.name}
                      </h3>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p className="text-gray-400">
                          {symptom.duration && (
                            <>
                              Duration:{" "}
                              <span className="text-gray-600">
                                {symptom.duration}
                              </span>{" "}
                              {symptom.severity && (
                                <span className="text-black">| </span>
                              )}
                            </>
                          )}
                          {symptom.severity && (
                            <>
                              Severity:{" "}
                              <span className="text-gray-600">
                                {symptom.severity}
                              </span>
                            </>
                          )}
                        </p>
                        {symptom.notes && (
                          <p className="text-gray-400">
                            Notes:{" "}
                            <span className="text-gray-600">
                              {symptom.notes}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medical History Section */}
            {summary?.medicalHistory?.length > 0 && (
              <div
                className={`p-4 bg-white rounded-lg mx-4 mt-4 shadow-sm ${
                  summary?.notes?.length > 0 || summary?.questions?.length > 0
                    ? ""
                    : "mb-[120px]"
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5">
                    <img
                      src={medicalHistory}
                      alt="medical history"
                      className="w-full h-full"
                    />
                  </div>
                  <h2 className="text-base font-medium text-gray-900">
                    Medical History
                  </h2>
                </div>
                <div className="space-y-6">
                  {summary?.medicalHistory?.map((item: any, index: any) => (
                    <div key={index} className="ml-3 relative pl-4">
                      <div className="absolute left-0 top-[10px] w-1.5 h-1.5 bg-gray-400 rounded-full mt-[-2px]"></div>

                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {item.name}
                      </h3>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p className="text-gray-400">
                          {item.duration && (
                            <>
                              Since:{" "}
                              <span className="text-gray-600">
                                {item.duration}
                              </span>{" "}
                              {(item.relation || item.notes) && (
                                <>
                                  <span className="text-black">|</span>{" "}
                                </>
                              )}
                            </>
                          )}
                          {item.relation && (
                            <>
                              Relation:{" "}
                              <span className="text-gray-600">
                                {item.relation}
                              </span>{" "}
                              {item.notes && (
                                <>
                                  <span className="text-black">|</span>{" "}
                                </>
                              )}
                            </>
                          )}
                          {item.notes && (
                            <>
                              Notes:{" "}
                              <span className="text-gray-600">
                                {item.notes}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {summary?.notes?.length > 0 && (
              <div
                className={`p-4 bg-white rounded-lg mx-4 mt-4 shadow-sm ${
                  summary?.questions?.length === 0 ? "mb-[100px]" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5">
                    <img src={others} alt="others" className="w-full h-full" />
                  </div>
                  <h2 className="text-base font-medium text-gray-900">Notes</h2>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  {summary?.notes}
                </div>
              </div>
            )}

            {/* Questions Section */}
            {summary?.questions?.length > 0 && (
              <div className="p-4 bg-white rounded-lg mx-4 mt-4 shadow-sm mb-[115px]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5">
                    <img
                      src={questions}
                      alt="questions"
                      className="w-full h-full"
                    />
                  </div>
                  <h2 className="text-base font-medium text-gray-900">
                    Questions to Doctor
                  </h2>
                </div>
                <div className="space-y-6">
                  {summary?.questions?.map((question: any, index: any) => (
                    <div key={index} className="ml-3 relative pl-4 pt-1">
                      <div className="absolute left-0 top-[10px] w-1.5 h-1.5 bg-gray-400 rounded-full mt-[-2px]"></div>

                      <h3 className="text-xs text-gray-600 mb-1">{question}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Success Message - full screen */}
      {showSuccess && (
        <div className="fixed inset-0 flex flex-col items-center justify-center z-50 bg-gradient-to-br from-pink-100 via-purple-100 to-indigo-100">
          <div className="flex flex-col items-center gap-6 px-4 text-center">
            <img
              src={successful}
              alt="success-animation"
              width={250}
              height={250}
            />
            <p className="text-[#1F2933] text-xl font-medium">
              Your symptoms has been
              <br />
              successfully shared with the Doctor!
            </p>
          </div>
        </div>
      )}

      {/* Fixed Submit Button */}
      {(isFromConversation || isFromHomeAppointment) &&
        isAppointmentInQueue &&
        !isLoading && (
          <div className="fixed bottom-0 left-0 right-0 bg-white shadow-top max-w-[450px] mx-auto z-20">
            <div className="p-4 flex flex-col gap-3 items-center">
              {showSubmitBtn ? (
                <>
                  <Button
                    className={`flex justify-center overflow-hidden gap-2 self-stretch px-14 py-2 text-white bg-indigo-600 whitespace-nowrap rounded-xl border border-indigo-600 border-solid w-full h-[50px] text-base font-medium`}
                    onClick={handleSubmitClick}
                  >
                    {LangSpecificMsgData.submitToDoctor}
                  </Button>

                  <div
                    className="text-[#4B4AD5] underline text-center flex items-center gap-2 cursor-pointer"
                    onClick={handleEditSC}
                  >
                    <img src={edit} width={20} height={20} alt="edit" />
                    <span>Edit/Add more details</span>
                  </div>
                </>
              ) : (
                <>
                  <button
                    className={`flex justify-center items-center overflow-hidden gap-2 self-stretch px-14 text-white bg-indigo-600 whitespace-nowrap rounded-xl border border-indigo-600 border-solid w-full h-[50px] text-base font-medium`}
                    onClick={handleEditSC}
                    aria-label="Add/Edit details"
                  >
                    <img src={editLight} alt="edit-light" className="w-5 h-5" />
                    {"Add/Edit details"}
                  </button>
                  <button
                    className="text-[#6B4DE0] font-medium underline"
                    onClick={() => {
                      const jwtToken = getLocalStorage(JWT_TOKEN);
                      navigate(`/appointment-booking?jwtToken=${jwtToken}`);
                    }}
                  >
                    Go Home
                  </button>
                  <div className="px-4 text-sm text-center text-[#BA7DE9]">
                    <span className="text-gray font-semibold">Note:</span> You
                    can still
                    <span className="text-gray font-semibold"> edit </span>
                    your details even after submitting, until your consultation
                    begins.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default Summary;
